import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Edit,
  Save,
  X,
  Plus,
  Trash2,
  GripVertical,
  Calendar,
  Clock,
  BookOpen,
  CheckCircle2,
} from 'lucide-react';

export default function RoadmapEditor({ roadmapRun, onSave, onCancel, isPro }) {
  const [editing, setEditing] = useState(false);
  const [roadmap, setRoadmap] = useState(roadmapRun?.roadmap || null);
  const [title, setTitle] = useState(roadmapRun?.title || '');
  const [subtitle, setSubtitle] = useState(roadmapRun?.subtitle || '');
  const [totalMonths, setTotalMonths] = useState(roadmapRun?.totalMonths || 6);
  const [items, setItems] = useState(roadmap?.items || []);
  const [saving, setSaving] = useState(false);

  if (!isPro) {
    return (
      <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-6 text-center">
        <div className="text-lg font-bold text-yellow-300 mb-2">Sign-in required</div>
        <div className="text-sm text-slate-300">Sign in to edit your saved roadmaps.</div>
      </div>
    );
  }

  const handleAddItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      title: 'New Item',
      description: '',
      duration: 4,
      month: 1,
      type: 'skill',
      coveredInBootcamp: false,
    };
    setItems([...items, newItem]);
  };

  const handleDeleteItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  const handleUpdateItem = (itemId, field, value) => {
    setItems(items.map(item => 
      item.id === itemId ? { ...item, [field]: value, ...(field === 'month' ? {
        startMonth: value, endMonth: value + Math.max(0, (item.endMonth || item.month) - (item.startMonth || item.month))
      } : {}) } : item
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!roadmap?._version || !roadmapRun?._version) throw new Error('Reload your roadmap before editing.');
      const client = generateClient();
      const cleanItems = items.map(item => ({id:item.id,title:item.title,duration:item.duration,month:item.month || item.startMonth || 1,
        startMonth:item.startMonth ?? null,endMonth:item.endMonth ?? null,cost:item.cost ?? null,
        description:item.description ?? null,type:item.type ?? null,coveredInBootcamp:item.coveredInBootcamp ?? false}));
      const result = await client.graphql({query:`mutation($input:UpdateRoadmapInput!){updateRoadmap(input:$input){id _version
        items{id title duration month startMonth endMonth cost description type coveredInBootcamp} certifications{id name cost duration}}}`,
        variables:{input:{id:roadmap.id,_version:roadmap._version,items:cleanItems,certifications:cleanItems.filter(item => item.type === 'certification').map(item => ({id:item.id,name:item.title,duration:item.duration,cost:item.cost || 0}))}},authMode:'userPool'});
      const updatedRoadmap = result.data?.updateRoadmap;
      if (result.errors?.length || !updatedRoadmap || updatedRoadmap.items.length !== cleanItems.length ||
        !cleanItems.every((item,index) => Object.keys(item).every(key => updatedRoadmap.items[index]?.[key] === item[key]))) {
        throw new Error('The roadmap changed on another device. Reopen it before saving again.');
      }
      setRoadmap(updatedRoadmap);
      const runResult = await client.graphql({query:`mutation($input:UpdateRoadmapRunInput!){updateRoadmapRun(input:$input){id _version roleKey roleName title subtitle totalMonths}}`,
        variables:{input:{id:roadmapRun.id,_version:roadmapRun._version,title,subtitle,totalMonths}},authMode:'userPool'});
      const savedRun = runResult.data?.updateRoadmapRun;
      if (runResult.errors?.length || !savedRun || savedRun.title !== title || savedRun.subtitle !== subtitle || savedRun.totalMonths !== totalMonths) {
        throw new Error('Items saved, but the roadmap heading could not be confirmed. Reopen the roadmap before trying again.');
      }
      const updatedRun = {...savedRun,roadmap:updatedRoadmap};

      if (onSave) onSave(updatedRun);
      setEditing(false);
    } catch (error) {
      console.error('Error saving roadmap:', error);
      alert(error.message || 'Changes could not be confirmed. Please reopen your roadmap.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-2 rounded-xl border border-green-400/30 bg-green-400/10 px-4 py-2 text-sm font-bold text-green-300 hover:bg-green-400/20 transition-colors"
      >
        <Edit className="h-4 w-4" />
        Edit Roadmap
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-xl font-extrabold text-white">Edit Roadmap</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-green-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-green-300 transition-colors disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setItems(roadmap?.items || []);
              setTitle(roadmapRun?.title || '');
              setSubtitle(roadmapRun?.subtitle || '');
              setTotalMonths(roadmapRun?.totalMonths || 6);
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-bold text-slate-300 hover:bg-slate-700 transition-colors"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
        </div>
      </div>

      {/* Roadmap Metadata */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-bold text-slate-300 mb-2">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white placeholder-slate-500 focus:border-green-400 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-300 mb-2">Subtitle</label>
          <input
            type="text"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white placeholder-slate-500 focus:border-green-400 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-slate-300 mb-2">Total Months</label>
          <input
            type="number"
            min="1"
            max="24"
            value={totalMonths}
            onChange={(e) => setTotalMonths(parseInt(e.target.value) || 6)}
            className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-white placeholder-slate-500 focus:border-green-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Items List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-extrabold text-white">Roadmap Items</div>
          <button
            type="button"
            onClick={handleAddItem}
            className="inline-flex items-center gap-2 rounded-xl border border-green-400/30 bg-green-400/10 px-4 py-2 text-sm font-bold text-green-300 hover:bg-green-400/20 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
        </div>

        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={item.id}
              className="rounded-xl border border-slate-800 bg-slate-900/50 p-4"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 pt-2">
                  <GripVertical className="h-5 w-5 text-slate-500" />
                </div>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-slate-400 mb-1">Title</label>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-green-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1">Month</label>
                    <input
                      type="number"
                      min="1"
                      max={totalMonths}
                      value={item.month}
                      onChange={(e) => handleUpdateItem(item.id, 'month', parseInt(e.target.value) || 1)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-green-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1">Duration (weeks)</label>
                    <input
                      type="number"
                      min="1"
                      value={item.duration}
                      onChange={(e) => handleUpdateItem(item.id, 'duration', parseInt(e.target.value) || 4)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-green-400 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-xs font-bold text-slate-400 mb-1">Description</label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-green-400 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-4 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input
                        type="checkbox"
                        checked={item.type === 'certification'}
                        onChange={(e) => handleUpdateItem(item.id, 'type', e.target.checked ? 'certification' : 'skill')}
                        className="rounded border-slate-600 bg-slate-800 text-green-400 focus:ring-green-400"
                      />
                      Certification
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input
                        type="checkbox"
                        checked={item.coveredInBootcamp || false}
                        onChange={(e) => handleUpdateItem(item.id, 'coveredInBootcamp', e.target.checked)}
                        className="rounded border-slate-600 bg-slate-800 text-green-400 focus:ring-green-400"
                      />
                      Covered in Bootcamp
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteItem(item.id)}
                  className="flex-shrink-0 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
